import { useEffect, useState } from "react";
import {
  Alert, Button, Empty, Input, Result, Space, Switch, Table, Tabs, Tag, Typography, message,
} from "antd";
import { CreditCard, Home, RefreshCw, Wifi, WifiOff, Zap } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { comingSoonLabel } from "../../utils/comingSoon";
import "./AdminBoothSettingsPage.css";

const { Title, Text } = Typography;

interface BoothPayment {
  payment_id: string;
  amount: number;
  method: string;
  status: "paid" | "pending" | "overdue";
  paid_at: string;
}

interface MyBooth {
  booth_id: string;
  booth_number: string;
  renter_name: string;
  specialty: string;
  rental_rate: number;
  billing_cycle: "weekly" | "bi-weekly" | "monthly";
  lease_start: string | null;
  lease_end: string | null;
  next_payment_due: string | null;
  status: "active" | "inactive" | "overdue";
  autopay_enabled: boolean;
  renter_routing_number: string;
  renter_account_number: string;
  collects_payments: boolean;
  card_processor: string | null;
  payment_history: BoothPayment[];
}

const PROCESSORS: { id: string; name: string; rate: string; color: string }[] = [
  { id: "square", name: "Square", rate: "2.6% + $0.10", color: "#000000" },
  { id: "stripe", name: "Stripe", rate: "2.9% + $0.30", color: "#4f46e5" },
  { id: "paypal", name: "PayPal Here", rate: "2.29% + $0.09", color: "#2563eb" },
  { id: "clover", name: "Clover", rate: "2.3% + $0.10", color: "#16a34a" },
];

function cycleLabel(c: string) {
  if (c === "weekly") return "/wk";
  if (c === "bi-weekly") return "/2wks";
  return "/mo";
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminBoothSettingsPage() {
  const [booth, setBooth] = useState<MyBooth | null | undefined>(undefined); // undefined = loading
  const [activeTab, setActiveTab] = useState("overview");

  const [autopayEnabled, setAutopayEnabled] = useState(false);
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [showAccount, setShowAccount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [selectedProcessor, setSelectedProcessor] = useState<string | null>(null);
  const [processorConnected, setProcessorConnected] = useState(false);
  const [processorSaving, setProcessorSaving] = useState(false);

  useEffect(() => {
    axiosClient.get("/me/booth").then(res => {
      const b: MyBooth = res.data;
      setBooth(b);
      setAutopayEnabled(b.autopay_enabled);
      setRoutingNumber(b.renter_routing_number ?? "");
      setAccountNumber(b.renter_account_number ?? "");
      setSelectedProcessor(b.card_processor ?? null);
      setProcessorConnected(!!b.card_processor);
    }).catch(() => setBooth(null));
  }, []);

  async function saveCardProcessor(processorId: string) {
    setProcessorSaving(true);
    try {
      await axiosClient.put("/me/booth/card-processor", { card_processor: processorId });
      setSelectedProcessor(processorId);
      setProcessorConnected(true);
      message.success("Card processor preference saved.");
    } catch {
      message.error("Failed to save card processor preference.");
    } finally {
      setProcessorSaving(false);
    }
  }

  async function saveAutopay() {
    if (autopayEnabled) {
      if (!/^\d{9}$/.test(routingNumber)) {
        message.error("Routing number must be exactly 9 digits.");
        return;
      }
      if (!accountNumber.trim()) {
        message.error("Account number is required.");
        return;
      }
    }
    setSaving(true);
    try {
      await axiosClient.put("/me/booth/autopay", {
        autopay_enabled: autopayEnabled,
        renter_routing_number: routingNumber,
        renter_account_number: accountNumber,
      });
      setSaved(true);
      message.success("Autopay settings saved.");
      setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      const err = e as { response?: { data?: string } };
      message.error(err.response?.data || "Failed to save autopay settings.");
    } finally {
      setSaving(false);
    }
  }

  if (booth === undefined) return null;

  if (booth === null) {
    return (
      <div className="booth-settings-page">
        <Result
          status="info"
          title="No active booth found"
          subTitle="Contact the salon owner to set up your booth account."
        />
      </div>
    );
  }

  return (
    <div className="booth-settings-page">
      <Title level={3} style={{ marginBottom: 20 }}>My Booth Account</Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "overview", label: (<span><Home size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Overview</span>) },
          // Card processing and autopay need a real payment processor/bank integration —
          // shown but disabled until one is connected.
          ...(booth.collects_payments ? [{ key: "cardpayments", disabled: true, label: comingSoonLabel(<span><CreditCard size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Card Payments</span>) }] : []),
          { key: "autopay", disabled: true, label: comingSoonLabel(<span><RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Autopay Setup</span>) },
        ]}
      />

      {activeTab === "overview" && (
        <div style={{ maxWidth: 640 }}>
          <div className="booth-settings-card">
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div className="booth-settings-avatar">
                {booth.renter_name.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>{booth.renter_name}</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>{booth.specialty} · {booth.booth_number}</div>
                {booth.autopay_enabled && (
                  <Tag color="blue" icon={<Zap size={10} />} style={{ marginTop: 4 }}>Autopay Active</Tag>
                )}
              </div>
            </div>
            <div className="booth-settings-grid">
              <div className="booth-settings-stat">
                <div className="booth-settings-stat-label">Rental Rate</div>
                <div className="booth-settings-stat-value">${booth.rental_rate}{cycleLabel(booth.billing_cycle)}</div>
              </div>
              <div className="booth-settings-stat">
                <div className="booth-settings-stat-label">Next Payment Due</div>
                <div className="booth-settings-stat-value">{fmtDate(booth.next_payment_due)}</div>
              </div>
              <div className="booth-settings-stat">
                <div className="booth-settings-stat-label">Lease Start</div>
                <div className="booth-settings-stat-value">{fmtDate(booth.lease_start)}</div>
              </div>
              <div className="booth-settings-stat">
                <div className="booth-settings-stat-label">Lease End</div>
                <div className="booth-settings-stat-value">{fmtDate(booth.lease_end)}</div>
              </div>
            </div>
          </div>

          <div className="booth-settings-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", fontWeight: 600 }}>
              Payment History
            </div>
            {booth.payment_history.length === 0 ? (
              <Empty description="No payment history yet." style={{ padding: 32 }} />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="payment_id"
                dataSource={booth.payment_history}
                columns={[
                  { title: "Date", dataIndex: "paid_at", render: (v: string) => fmtDate(v.slice(0, 10)) },
                  { title: "Method", dataIndex: "method", render: (v: string) => <span style={{ textTransform: "capitalize" }}>{v}</span> },
                  { title: "Amount", dataIndex: "amount", align: "right", render: (v: number) => `$${v.toFixed(2)}` },
                  {
                    title: "Status", dataIndex: "status", align: "right",
                    render: (v: string) => (
                      <Tag color={v === "paid" ? "green" : v === "overdue" ? "red" : "orange"}>{v}</Tag>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === "cardpayments" && booth.collects_payments && (
        <div style={{ maxWidth: 480 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Demo only"
            description="This saves your processor preference, but no live payment processor is connected yet — card charges here aren't real. We'll wire up a real connection once a processor is chosen for the whole platform."
          />
          <Text type="secondary">
            You collect client card payments directly at the chair. Pick which processor you use so the salon knows how your payouts work.
          </Text>

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {PROCESSORS.map(p => (
              <button
                key={p.id}
                onClick={() => saveCardProcessor(p.id)}
                disabled={processorSaving}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 10, textAlign: "left",
                  border: selectedProcessor === p.id ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                  background: selectedProcessor === p.id ? "#f5f3ff" : "#fff", cursor: "pointer",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, background: p.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <CreditCard size={16} color="#fff" />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{p.rate}</div>
                </div>
              </button>
            ))}
          </div>

          {selectedProcessor && (
            <div style={{
              marginTop: 16, borderRadius: 10, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between",
              border: `2px solid ${processorConnected ? "#bbf7d0" : "#e5e7eb"}`, background: processorConnected ? "#f0fdf4" : "#fafafa",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {processorConnected ? <Wifi size={16} color="#16a34a" /> : <WifiOff size={16} color="#9ca3af" />}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {PROCESSORS.find(p => p.id === selectedProcessor)?.name} selected
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>Preference saved — no live account connected</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "autopay" && (
        <div style={{ maxWidth: 480 }}>
          <Text type="secondary">
            Authorize direct deposit payments from your bank account to the salon every{" "}
            {booth.billing_cycle === "weekly" ? "week" : booth.billing_cycle === "bi-weekly" ? "2 weeks" : "month"}.
          </Text>

          <div className="booth-autopay-toggle">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="booth-autopay-icon"><Zap size={16} color="#fff" /></div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Autopay Direct Deposit</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  ${booth.rental_rate} · every {booth.billing_cycle === "weekly" ? "week" : booth.billing_cycle === "bi-weekly" ? "2 weeks" : "month"}
                </div>
              </div>
            </div>
            <Switch checked={autopayEnabled} onChange={setAutopayEnabled} />
          </div>

          <div style={{ opacity: autopayEnabled ? 1 : 0.4, pointerEvents: autopayEnabled ? "auto" : "none", marginTop: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <CreditCard size={13} /> Your Bank Account
            </div>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Routing Number * (9 digits)</div>
                <Input
                  value={routingNumber}
                  onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="123456789"
                  style={{ fontFamily: "monospace" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Account Number *</div>
                <Input.Password
                  value={accountNumber}
                  onChange={e => setAccountNumber(e.target.value)}
                  placeholder="Account number"
                  visibilityToggle={{ visible: showAccount, onVisibleChange: setShowAccount }}
                  style={{ fontFamily: "monospace" }}
                />
              </div>
            </Space>
          </div>

          <div className="booth-autopay-notice">
            Bank details are stored for autopay processing between you and the salon.
          </div>

          <Button
            type="primary"
            block
            size="large"
            icon={<RefreshCw size={15} />}
            loading={saving}
            onClick={saveAutopay}
            style={{ marginTop: 16 }}
          >
            {saved ? "Settings Saved!" : "Save Autopay Settings"}
          </Button>
        </div>
      )}
    </div>
  );
}
